import { useEffect, useState, useRef } from "react";
import { motion } from "framer-motion";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { ChevronDown } from "lucide-react";
import NotFound from "@/pages/NotFound";
import { projectBGS as projects, dropdownBGS as dropdowns } from "../constants";
import { Link } from "react-router-dom";

gsap.registerPlugin(ScrollTrigger);

const ServiceBGS = () => {
  if (!projects || !dropdowns) return <NotFound />;

  const sectionRef = useRef<HTMLDivElement>(null);
  const [openDropdown, setOpenDropdown] = useState<number | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (!projects) return;

    const ctx = gsap.context(() => {
      const slides = gsap.utils.toArray<HTMLElement>(".image-slide");

      // Header parallax animation
      gsap.fromTo(
        ".header-text",
        { y: 100, opacity: 0 },
        {
          y: 0,
          opacity: 1,
          duration: 1,
          scrollTrigger: {
            trigger: ".header-text",
            start: "top 80%",
          },
        },
      );

      // Animate text section
      gsap.from(".service-text", {
        y: 60,
        opacity: 0,
        duration: 1,
        ease: "power3.out",
        scrollTrigger: {
          trigger: ".service-text",
          start: "top 80%",
        },
      });

      // ✅ Only run pinning timeline on desktop
      ScrollTrigger.matchMedia({
        "(min-width: 0px)": () => {
          const tl = gsap.timeline({
            scrollTrigger: {
              trigger: sectionRef.current,
              start: "top top",
              end: `+=${projects.length * 800}vh`,
              scrub: true,
              pin: sectionRef.current,
              anticipatePin: 1,
            },
          });

          slides.forEach((slide, i) => {
            if (i === 0) {
              tl.set(slide, { opacity: 1, zIndex: 10 });
            } else {
              tl.to(slides[i - 1], { opacity: 0, duration: 0.5, zIndex: 0 }, i);
              tl.to(slide, { opacity: 1, duration: 0.5, zIndex: 10 }, i);
            }
          });
        },
      });
    }, sectionRef);

    return () => ctx.revert();
  }, [projects]);

  return (
    <section ref={sectionRef} className="relative overflow-hidden">
      <div
        className="flex flex-col md:flex-row items-start justify-around 
        px-4 md:px-20 lg:px-40 2xl:px-48 pt-20 md:pt-28 
        h-auto md:h-[100vh] bg-[#f5f5f5]"
      >
        {/* left: Larger Pinned Images */}
        <div className="scroll-images w-full md:w-[500px] xl:w-[600px] flex justify-center items-center mb-4 md:mt-20 md:mr-10">
          <div className="image-stack relative min-w-full min-h-[400px] md:min-w-[500px] md:min-h-[500px] xl:min-w-[600px] xl:min-h-[600px]">
            {projects.map((project, i) => (
              <Link
                key={i}
                to={`?id=${project.id}`}
                className={`image-slide absolute top-0 left-0 w-full h-full rounded-2xl overflow-hidden shadow-lg 
                  ${i === 0 ? "opacity-100 md:opacity-0" : "opacity-0"}`}
              >
                {/* Image */}
                <img
                  src={project.image}
                  alt={`service-${i}`}
                  className="w-full h-full object-cover"
                />
                <div className="absolute bottom-0 left-0 w-full h-1/4 bg-gradient-to-t from-[#0d1821]/70 to-transparent pointer-events-none"></div>
                {/* Title overlay */}
                <div className="absolute bottom-4 left-4 bg-transparent text-[#f5f5f5] text-xl font-poppins font-medium text-left">
                  {project.title}
                </div>
              </Link>
            ))}
          </div>
        </div>

        {/* right: Text */}
        <div className="service-text h-[1000px] w-full md:h-auto sm:w-[400px] md:w-[600px] xl:w-[700px] px-4">
          <h1
            className="header-text font-medium text-[#0d1821] text-4xl mb-3 lg:mb-6 font-orbitron leading-tight"
            data-speed="1.2"
          >
            Brand Growth Solution
          </h1>
          <p
            className="text-justify md:text-left text-md md:text-lg text-[#0d1821]/80 mb-4 lg:mb-6 max-w-2xl mx-auto lg:mx-0 font-poppins"
            data-speed="1.1"
          >
            Our team of strategists, designers, video editors, and animators
            helps you grow your digital presence and reach your business goals
            with everyday content as well as big campaign solutions.
          </p>

          {/* Dropdown Menus */}
          <section className="space-y-4 w-full max-w-[700px] mb-7">
            {dropdowns.map((item) => (
              <div
                key={item.id}
                className="border rounded-2xl shadow-sm overflow-hidden"
              >
                <button
                  className="w-full flex justify-between items-center px-6 py-2 md:py-2 text-md md:text-lg font-bold text-[#0d1821] font-poppins text-left"
                  onClick={() =>
                    setOpenDropdown(openDropdown === item.id ? null : item.id)
                  }
                >
                  {item.title}
                  <ChevronDown
                    className={`transition-transform ${
                      openDropdown === item.id ? "rotate-180" : ""
                    }`}
                  />
                </button>
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={
                    openDropdown === item.id
                      ? { height: "auto", opacity: 1 }
                      : { height: 0, opacity: 0 }
                  }
                  transition={{ duration: 0.3 }}
                  className="px-6 text-gray-600 overflow-hidden will-change-[height]"
                >
                  <p className="py-3 leading-relaxed font-poppins text-[#0d1821]/80 text-md">
                    {item.content}
                  </p>
                </motion.div>
              </div>
            ))}
          </section>
        </div>
      </div>
    </section>
  );
};

export default ServiceBGS;
